import { Card, Divider } from "react-native-paper";
import { buttonRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";
import type { AlignItems } from "../Box";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { Skeleton } from "../Skeleton";

const SUCCESS_ICON_CONTAINER_SIZE = 72;
const PRODUCT_ICON_SIZE = 56;
const FEATURE_ICON_SIZE = 30;
const HERO_TITLE_LINE_HEIGHT = 24;
const PRODUCT_TITLE_LINE_HEIGHT = 20;
const BODY_LINE_HEIGHT = 20;
const LABEL_LINE_HEIGHT = 16;
const BUTTON_HEIGHT = 40;

const LAST_TEXT_LINE_WIDTH = "60%";

const TextLine = ({
	lineHeight,
	barHeight,
	width,
	align = "flex-start",
}: {
	lineHeight: number;
	barHeight: number;
	width: number | `${number}%`;
	align?: AlignItems;
}) => (
	<Box
		width="100%"
		height={lineHeight}
		justifyContent="center"
		alignItems={align}
	>
		<Skeleton width={width} height={barHeight} />
	</Box>
);

const TextBlock = ({
	numLines,
	lineHeight,
	barHeight,
	align,
}: {
	numLines: number;
	lineHeight: number;
	barHeight: number;
	align?: AlignItems;
}) => {
	if (numLines < 1) {
		return null;
	}
	return (
		<Box width="100%">
			{Array.from({ length: numLines }).map((_, index) => (
				<TextLine
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
					key={index}
					lineHeight={lineHeight}
					barHeight={barHeight}
					width={index === numLines - 1 ? LAST_TEXT_LINE_WIDTH : "100%"}
					align={align}
				/>
			))}
		</Box>
	);
};

export type PurchaseSuccessSkeletonProduct = {
	/** Feature rows to reserve; `0` omits the divider and the feature block */
	numFeatures?: number;
};

type Props = {
	/** One entry per product card. Pass the real products once they are known. */
	products?: PurchaseSuccessSkeletonProduct[];
	numActions?: number;
	titleLines?: number;
	summaryLines?: number;
};

const DEFAULT_PRODUCTS: PurchaseSuccessSkeletonProduct[] = [{ numFeatures: 4 }];

export const PurchaseSuccessSkeleton = ({
	products = DEFAULT_PRODUCTS,
	numActions = 2,
	titleLines = 2,
	summaryLines = 2,
}: Props) => {
	const theme = useAppTheme();

	const buttonRadius = theme.roundness * buttonRoundnessMultiplier;

	return (
		<Box gap={16}>
			<Box gap={12} alignItems="center">
				<Skeleton size={SUCCESS_ICON_CONTAINER_SIZE} variant="circle" />
				<TextBlock
					numLines={titleLines}
					lineHeight={HERO_TITLE_LINE_HEIGHT}
					barHeight={18}
					align="center"
				/>
				<TextBlock
					numLines={summaryLines}
					lineHeight={BODY_LINE_HEIGHT}
					barHeight={14}
					align="center"
				/>
			</Box>
			{products.map(({ numFeatures = 4 }, productIndex) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cards
				<FlatCard key={productIndex}>
					<Card.Content style={{ padding: 16 }}>
						<Box flexDirection="row" alignItems="center" gap={16}>
							<Skeleton size={PRODUCT_ICON_SIZE} />
							<Box flex={1} minWidth={0} gap={2}>
								<TextLine
									lineHeight={PRODUCT_TITLE_LINE_HEIGHT}
									barHeight={16}
									width="75%"
								/>
								<TextLine
									lineHeight={LABEL_LINE_HEIGHT}
									barHeight={12}
									width="40%"
								/>
							</Box>
						</Box>
					</Card.Content>
					{numFeatures > 0 ? (
						<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />
					) : null}
					{numFeatures > 0 ? (
						<Card.Content style={{ padding: 16, gap: 8 }}>
							{Array.from({ length: numFeatures }).map((_, index) => (
								<Box
									// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
									key={index}
									flexDirection="row"
									alignItems="center"
									gap={8}
								>
									<Skeleton size={FEATURE_ICON_SIZE} variant="circle" />
									<Box flex={1} minWidth={0}>
										<TextLine
											lineHeight={BODY_LINE_HEIGHT}
											barHeight={14}
											width="100%"
										/>
									</Box>
								</Box>
							))}
						</Card.Content>
					) : null}
				</FlatCard>
			))}
			<Box gap={8}>
				{Array.from({ length: numActions }).map((_, index) => (
					<Skeleton
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
						key={index}
						height={BUTTON_HEIGHT}
						borderRadius={buttonRadius}
					/>
				))}
			</Box>
		</Box>
	);
};
